/**
 * Risks Service - Thalassa API
 * 
 * RESTful service for Risk Register management
 * Uses new Thalassa API endpoints
 */
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';
import { Risk } from '@/types/risks';
import { window } from '@/js/external-js/variableModules/utils/window.util';

// API base path - uses Thalassa internal API
const RISKS_API = '/v1/risks';

/**
 * Transform raw risk data from API to Risk type
 */
const transformRisk = (risk: any): Risk => ({
  ...risk,
  id: risk.componentId || risk.riskID,
  riskID: risk.riskID,
  componentId: risk.componentId,
  description: risk.risk || risk.description || '',
  riskRating: risk.riskRating || 'Medium',
  category: risk.team || risk.businessUnit,
  lastReviewDate: risk.lastReviewDate ? (new Date(risk.lastReviewDate)) : risk.lastReview ? (new Date(risk.lastReview)) : 'No Date Set',
  nextReviewDate: risk.nextReviewDate ? (new Date(risk.nextReviewDate)) : risk.nextReview ? (new Date(risk.nextReview)) : 'No Date Set',
  treatmentRequired: risk.treatRequired || risk.treatmentRequired || 'No',
  relatedControls: [...(risk.relatedControls || []), ...(Array.isArray(risk.selectedControls) ? risk.selectedControls : [])],
});

/**
 * Get all risks for a company
 */
export async function getAllRisks(companyID: string): Promise<Risk[]> {
  const res = await deiterateAPI.GET(RISKS_API, {
    mode: 'cors',
    backend: 'thalassa',
    headers: {
      'X-Tenancy-Id': companyID,
      'Company-ID': companyID,
    }
  });

  return (res.risks || []).map(transformRisk);
}

/**
 * Get a single risk by ID
 */
export async function getRisk(companyID: string, riskID: string): Promise<Risk> {
  console.log('[risksService]', 'getRisk', riskID);
  
  const res = await deiterateAPI.GET(`${RISKS_API}/${riskID}`, {
    mode: 'cors',
    backend: 'thalassa',
    headers: {
      'X-Tenancy-Id': companyID,
      'Company-ID': companyID,
    }
  });
  
  const risk = res.entry;
  console.log('[risksService]', 'getRisk', res.entry);

  return {
    ...transformRisk(risk),
    menus: res.menu || {},
  } as Risk;
}

/**
 * Update an existing risk
 */
export async function putRisk(companyID: string, riskID: string, riskItem: Risk): Promise<Risk> {
  console.log('[risksService]', 'putRisk', companyID, riskItem);
  
  // Clean up legacy fields
  const cleanedRisk = { ...riskItem };
  if (cleanedRisk.nextReview && cleanedRisk.nextReviewDate) {
    delete cleanedRisk.nextReview;
  }
  if (cleanedRisk.lastReview && cleanedRisk.lastReviewDate) {
    delete cleanedRisk.lastReview;
  }
  if (cleanedRisk.treatmentRequired && cleanedRisk.treatRequired) {
    delete cleanedRisk.treatRequired;
  }

  const res = await deiterateAPI.PUT(`${RISKS_API}/${riskID}`, {
    mode: 'cors',
    backend: 'thalassa',
    headers: {
      'X-Tenancy-Id': companyID,
      'Company-ID': companyID,
    },
    body: JSON.stringify(cleanedRisk)
  });

  return {
    ...res,
    id: riskItem.id,
    description: riskItem.risk || riskItem.description || '',
    riskRating: riskItem.riskRating || 'Medium',
    category: riskItem.category || '',
    lastReviewDate: new Date(riskItem.lastReviewDate),
    nextReviewDate: new Date(riskItem.nextReviewDate),
    treatmentRequired: riskItem.treatmentRequired || riskItem.treatRequired || 'No',
    status: riskItem.status,
  } as Risk;
}

/**
 * Create a new risk
 */
export async function createRisk(companyID: string, riskID: string, riskItem: Risk): Promise<Risk> {
  console.log('[risksService]', 'createRisk', companyID, riskItem);

  const res = await deiterateAPI.POST(RISKS_API, {
    mode: 'cors',
    backend: 'thalassa',
    headers: {
      'X-Tenancy-Id': companyID,
      'Company-ID': companyID,
    },
    body: JSON.stringify({ ...riskItem, riskID: parseInt(riskID) })
  });

  return {
    ...res,
    id: riskItem.id,
    componentId: res.componentId,
    description: riskItem.risk || riskItem.description || '',
    riskRating: riskItem.riskRating || 'Medium',
    category: riskItem.category || '',
    nextReviewDate: new Date(riskItem.nextReviewDate),
    status: riskItem.status,
  } as Risk;
}

/**
 * Delete a risk
 */
export async function deleteRisk(companyID: string, riskID: string): Promise<void> {
  await deiterateAPI.DELETE(`${RISKS_API}/${riskID}`, {
    mode: 'cors',
    backend: 'thalassa',
    headers: {
      'X-Tenancy-Id': companyID,
      'Company-ID': companyID,
    }
  });
}

/**
 * Get risk menus/dropdowns
 */
export async function getMenus() {
  console.log('[DEBUG] getMenus called for companyID:', window.companyID);
  
  const res = await deiterateAPI.GET(`${RISKS_API}/menu`, {
    mode: 'cors',
    backend: 'thalassa',
    headers: {
      'X-Tenancy-Id': window.companyID,
      'Company-ID': window.companyID,
    }
  });
  
  console.log('[DEBUG] getMenus response:', res);
  console.log('[DEBUG] getMenus riskCategory:', res.menu?.riskCategory);

  return res.menu;
}
